import { Dialog } from "@headlessui/react";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { GridRow } from "../../components/table/TableList";
import { SetStateAction, useRef } from "react";
import { Company } from "../../model/Companies";
import { convertDateTimeToStringCompactFormat } from "../../utility/ConvertUtily";
import { Messages } from "../../model/Message";

export type QuestionnaireResultsExportModalProps = {
  gridRow: GridRow[];
  company: Company | undefined;
  message: Messages;
  questionnaire: {
    target?: string;
    title1?: string;
    title2?: string;
    title3?: string;
    answers1?: string[];
    answers2?: string[];
    answers3?: string[];
  };
  stateSetLoadProgress: (value: SetStateAction<number>) => void;
}

export const QuestionnaireResultsExportModal = ({
  open,
  toggle,
  closeOnClickOutside,
  gridRow,
  company,
  message,
  questionnaire,
  stateSetLoadProgress
}: ModalProp & QuestionnaireResultsExportModalProps) => {

  const csvDataLength = useRef<number>(10000);
  const csvCol = [
    "利用者番号",
    "名前",
    "出欠結果",
    "回答状況",
    questionnaire.title1 || "",
    ...(questionnaire.title2 ? [questionnaire.title2] : []),
    ...(questionnaire.title3 ? [questionnaire.title3] : []),
  ];

  async function exportCsvHandler () {
    toggle();
    setLoadProgress(0);
    const result = await exportCsv();
    console.log(result);
    setLoadProgress(-1);
  }

  async function exportCsv () {
    try {
      const rowData = gridRow.map((data) => data.rowData);
      const csvData = rowData.map((value) => {
        return [
          value.id || "",
          value.userName || "",
          value.attendanceButtonName || "",
          value.answerResult || "",
          value.answer1 || "",
          ...(questionnaire.title2 ? [value.answer2 || ""] : []),
          ...(questionnaire.title3 ? [value.answer3 || ""] : []),
        ];
      });
  
      // 出力データを出力件数ずつ分割する
      const csvDataList = csvData.reduce((preArr: any[][], current, index) => {
        if (index % csvDataLength.current) { return preArr } else { return [...preArr, ...[csvData.slice(index, index + csvDataLength.current)]] }
      },[] as any[][]);
  
      for (let i = 0; i < csvDataList.length ; i++) {
        // 文字列のエスケープ処理
        const escapeCsvData = csvDataList[i].map((value: any[], index: number) => {
          setLoadProgress((index / rowData.length * 100) / 2);
          return value.map((val) => {
            return `"${(val || "").replace(/"/g, '""')}"`;
          });
        });
        const escapeCsvCol = csvCol.map((value) => {
          return `"${value.replace(/"/g, '""')}"`;
        });
  
        // カラムとデータを連結
        const csvStr = escapeCsvCol.join(',') + "\n" + escapeCsvData.map((data: any[]) => data.join(',')).join('\n');
  
        // ファイル名
        let filename = `アンケート結果_${company!.name}_${message!.title}_${convertDateTimeToStringCompactFormat(new Date())}${csvDataList.length > 1 ? '_' + (i + 1) : ''}.csv`;
  
        const a = document.createElement('a');
        // BOM
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvStr], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.id = "download-anchor";
        a.style.display = 'none';
  
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.setAttribute('sandbox', 'allow-same-origin allow-downloads');
  
        document.body.appendChild(iframe);
        if (iframe.contentWindow) {
          const iDoc = iframe.contentWindow.document;
          iDoc.write(a.outerHTML);
          const targetAnchor = iDoc.getElementById('download-anchor');
          targetAnchor?.click();
        }
        iframe.remove();
  
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 500);
      }
  
      return { result: true, details: undefined };
    } catch (err) {
      return { result: false, details: err };
    }
  }

  function setLoadProgress (percentage: number) {
    stateSetLoadProgress(percentage);
  }

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
        <div className="px-2 pb-4 sm:p-4">
          <div className="text-center sm:mt-3">
            <Dialog.Title as="h3" className="text-left text-lg font-medium leading-6 text-gray-900">
              結果出力
            </Dialog.Title>
          </div>
          <div className="mt-5">
            アンケートの結果情報をCSVファイルで出力します。<br />
            <span className="text-red-500">
              ※出力処理操作は、対象データの件数によって<span className="font-semibold">作業時間が長時間になる場合があります</span>。
            </span>
          </div>
        </div>
        <ModalFoterBox>
          <Button
            className="w-[132px] mt-1 mr-2"
            outline
            onClick={toggle}
            children={"キャンセル"}
          />
          <Button
            className="w-[132px] mt-1 mr-2"
            onClick={exportCsvHandler}
            children={"OK"}
          />
        </ModalFoterBox>
      </Dialog.Panel>
    </Modal>
  );
}